import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Stack, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';
import ConfirmDialog from 'src/components/confirm-dialog';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { getTankCategories, addTankCategory, deleteTankCategory, updateTankCategory } from 'src/api';

interface TankType {
  id: string;
  name: string;
  total?: number;
}

const DEFAULT_ROWS_PER_PAGE = 5;

export default function TankTypeList() {
  const [rows, setRows] = useState<TankType[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = useState(0);
  const [delItem, setDelItem] = useState<TankType | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editItem, setEditItem] = useState<TankType | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // 获取分类列表 - 支持分页
  const fetchCategories = async (currentPage: number, pageSize: number) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage + 1, // 后端通常从1开始计数
        pageSize: pageSize
      };
      const response: any = await getTankCategories(params);
      
      // 根据实际API响应格式调整: { code: 0, data: { data: TankType[] } }
      if (response.code === 200 && response.data && response.data.data) {
        const categories = response.data.data;
        setRows(Array.isArray(categories) ? categories : []);
        // 如果后端没有返回总数，暂时使用当前数据长度
        // 注意：这种情况下分页功能会受限，建议后端返回总数
        setTotal(response.data.totalElements);
      } else if (Array.isArray(response)) {
        // 如果直接返回数组，则没有分页信息
        setRows(response);
        setTotal(response.length);
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('获取分类列表失败:', error);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

 

  // 分页变化时重新获取数据
  useEffect(() => {
    fetchCategories(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // 重置到第一页
  };
  
  const handleDelete = (item: TankType) => {
    setDelItem(item);
    setOpenConfirm(true);
  };

  // 处理编辑点击
  const handleEdit = (item: TankType) => {
    setEditItem(item);
    setIsEditMode(true);
    // 设置表单默认值为当前编辑项的数据
    reset({ name: item.name });
    setOpenAdd(true); // 复用添加对话框
  };
  
  const handleConfirmDelete = async () => {
    if (delItem) {
      try {
        await deleteTankCategory(delItem.id);
        
        // 删除后检查当前页是否还有数据
        const newTotal = total - 1;
        const maxPage = Math.max(0, Math.ceil(newTotal / rowsPerPage) - 1);
        const targetPage = page > maxPage ? maxPage : page;
        
        // 如果需要跳转页面，先更新页面状态
        if (targetPage !== page) {
          setPage(targetPage);
        }
        
        // 重新获取数据
        await fetchCategories(targetPage, rowsPerPage);
        setOpenConfirm(false);
        setDelItem(null);
      } catch (error) {
        console.error('删除分类失败:', error);
        // 这里可以添加错误提示
      }
    }
  };

  // 新增分类表单
  const defaultValues = { name: '' };
  const methods = useForm({ defaultValues });
  const { reset, handleSubmit } = methods;

  const handleAdd = async (data: { name: string }) => {
    try {
      setAddLoading(true);
      
      if (isEditMode && editItem) {
        // 编辑模式
        await updateTankCategory(editItem.id, data);
        // 编辑成功后重新获取当前页数据
        await fetchCategories(page, rowsPerPage);
      } else {
        // 添加模式
        await addTankCategory(data);
        // 添加成功后重新获取第一页数据
        setPage(0);
        await fetchCategories(0, rowsPerPage);
      }
      
      // 关闭对话框并重置状态
      setOpenAdd(false);
      reset();
      setIsEditMode(false);
      setEditItem(null);
    } catch (error) {
      console.error(isEditMode ? '编辑分类失败:' : '添加分类失败:', error);
      // 这里可以添加错误提示
    } finally {
      setAddLoading(false);
    }
  };

  // 处理对话框关闭
  const handleCloseDialog = () => {
    setOpenAdd(false);
    reset();
    setIsEditMode(false);
    setEditItem(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
        鱼缸一级分类
      </Typography>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} mb={2}>
        <Button variant="contained" onClick={() => {setOpenAdd(true);reset({ name:"" });}}>
          添加分类
        </Button>
      </Stack>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="tank type table">
            <TableHead>
              <TableRow>
                <TableCell align="center">分类名称</TableCell>
                <TableCell align="center">二级分类数量</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell align="center">{row.name}</TableCell>
                    <TableCell align="center">{row.total || 0}</TableCell>
                    <TableCell align="center">
                      <Button variant="text" onClick={() => handleEdit(row)}>编辑</Button>
                      <Button variant="text" color="error" onClick={() => handleDelete(row)}>删除</Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">暂无数据</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={'每页条数'}
        />
      </Paper>
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title="删除"
        content={`确定要删除分类"${delItem?.name}"吗？`}
        action={
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            删除
          </Button>
        }
      />
      <ConfirmDialog
        open={openAdd}
        onClose={handleCloseDialog}
        title={isEditMode ? "编辑分类" : "添加分类"}
        content={
          <FormProvider methods={methods} onSubmit={handleSubmit(handleAdd)}>
            <RHFTextField name="name" label="分类名称" sx={{ mt: 2, mb: 1 }} />
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              sx={{ mt: 2 }}
              disabled={addLoading}
            >
              {addLoading ? <CircularProgress size={20} /> : (isEditMode ? '确认修改' : '确认添加')}
            </Button>
          </FormProvider>
        }
        action={null}
      />
    </Box>
  );
}